import { isEmpty } from "lodash";
import { z } from "zod";
import { EmployeeTrainingStatus } from "../constants";
import { SELECTED_FILE_TYPE } from "../interfaces/Practical";

export const AddTrainingSchema = z
  .object({
    integrationCourse: z
      .object({
        id: z.string(),
        name: z.string()
      })
      .nullable()
      .refine((data) => !isEmpty(data) && !!data.id, {
        message: "Training is required"
      }),
    integration: z
      .object({
        id: z.string(),
        name: z.string()
      })
      .optional()
      .nullable(),
    completionDate: z.string().nullable().optional(),
    state: z.string().optional(),
    commentText: z.string().optional(),
    formFiles: z.any(),
    selectedFileType: z.enum([SELECTED_FILE_TYPE.DOCUMENT, SELECTED_FILE_TYPE.CHECKLIST]),
    checklistAnswers: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        answers: z.array(z.any()),
        assignmentId: z.string().optional()
      })
    ),
    deletedDocumentIds: z.array(z.string()),
    deletedChecklistAssignmentIds: z.array(z.string())
  })
  .superRefine((data, ctx) => {
    // Completion date is only required when the training is being marked as Approved.
    // For non-approved states (pending / needs feedback) the date is optional.
    const isApproved = data.state === EmployeeTrainingStatus.Approved.toString();
    if (isApproved && isEmpty(data.completionDate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["completionDate"],
        message: "Completion date is required"
      });
    }
  });
// The training status (state) field is only shown to instructors. An employee adding their own
// training is submitting a pending request, so the completion date is optional for them. It is
// only required when an instructor explicitly marks the training as Approved.
export const buildAddEmployeeTrainingSchema = (isInstructorViewing: boolean) =>
  z
    .object({
      integrationCourse: z
        .object({
          id: z.string(),
          name: z.string()
        })
        .optional()
        .nullable()
        .refine((data) => !isEmpty(data), {
          message: "Training is required"
        }),
      completionDate: z.string().nullable().optional(),
      state: z.string().optional(),
      commentText: z.string().optional(),
      formFiles: z.any(),
      selectedFileType: z.enum([SELECTED_FILE_TYPE.DOCUMENT, SELECTED_FILE_TYPE.CHECKLIST]),
      checklistAnswers: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          answers: z.array(z.any()),
          assignmentId: z.string().optional()
        })
      ),
      deletedDocumentIds: z.array(z.string()),
      deletedChecklistAssignmentIds: z.array(z.string())
    })
    .superRefine((data, ctx) => {
      const isApproved = data.state === EmployeeTrainingStatus.Approved.toString();
      if (isInstructorViewing && isApproved && isEmpty(data.completionDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["completionDate"],
          message: "Completion date is required"
        });
      }
    });

// Default instance (instructor view) used for type inference and non-instructor callers.
export const AddEmployeeTrainingSchema = buildAddEmployeeTrainingSchema(true);

export const CreateTrainingSchema = z.object({
  trainingName: z
    .string()
    .min(2, "settingsViews.training.validationNameMin")
    .max(300, "settingsViews.training.validationNameMax"),
  requiredFaculties: z.string().optional(),
  recurrence: z
    .object({
      value: z.number()
    })
    .nullable()
    .optional(),
  selectedCompetencies: z.array(z.any()).default([]),
  selectedIntegrationId: z
    .object({
      id: z.string(),
      name: z.string()
    })
    .nullable()
    .optional()
    .refine((val) => val !== null && val !== undefined && val.name !== "", {
      message: "Integration is required"
    })
});

export const UploadJobDescriptionSchema = z.object({
  file: z
    .instanceof(File)
    .nullable()
    .refine((file) => {
      const fileTypes = ["application/pdf"];
      return file && fileTypes.includes(file?.type);
    }, "Please upload a valid file")
});

export const JobBenchMarkingSchema = z.object({
  jobDescription: z.string().optional(),
  renamedTasks: z
    .array(
      z.object({
        id: z.string(),
        name: z.string()
      })
    )
    .optional(),
  renamedCompetencies: z
    .array(
      z.object({
        id: z.string(),
        name: z.string()
      })
    )
    .optional(),
  addedStandardMatchedTasks: z
    .array(
      z.object({
        id: z.string(),
        name: z.string()
      })
    )
    .optional(),
  addedStandardMatchedTaskCompetencies: z
    .array(
      z.object({
        id: z.string(),
        name: z.string()
      })
    )
    .optional(),
  addedStandardMatchedCompetencies: z
    .array(
      z.object({
        id: z.string(),
        name: z.string()
      })
    )
    .optional(),
  addedStandardUnmatchedTasks: z
    .array(
      z.object({
        id: z.string(),
        name: z.string()
      })
    )
    .optional(),
  addedStandardUnmatchedCompetencies: z
    .array(
      z.object({
        id: z.string(),
        name: z.string()
      })
    )
    .optional()
});

export const EnrolStudentsSchema = z.object({
  selectedCourses: z.array(z.any()).min(1, "Please select a course"),
  selectedStudents: z.array(z.any()).min(1, "Please select a student"),
  paymentOption: z.enum(["useBalance", "purchaseSeparately"])
});

export const InviteNewEmployeeSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().min(1, "Email address is required").email("Please enter a valid email address")
});

export type UploadJobDescriptionType = z.infer<typeof UploadJobDescriptionSchema>;
export type JobBenchMarkingType = z.infer<typeof JobBenchMarkingSchema>;
export type EnrolStudentsType = z.infer<typeof EnrolStudentsSchema>;
export type InviteNewEmployeeType = z.infer<typeof InviteNewEmployeeSchema>;
